// store/subscriptionListsCacheStore.ts
import { create } from 'zustand'
import { persist } from 'zustand/middleware'

export type SortType = 'name' | 'recently_added'
export type OrderType = 'asc' | 'desc'

export type ListRow = {
  id: string
  name: string
  slug?: string
  is_all?: boolean
  created_at?: string
  createdAt?: string
  count?: number
  avatars?: string[]
}

type WrappedList = { data: Partial<ListRow> }

type CacheEntry = {
  key: string
  sort: SortType
  order: OrderType
  lists: WrappedList[]
  countsById: Record<string, number>
  updatedAt: number
  fetchedAt?: number
}

type StoreState = {
  entries: Record<string, CacheEntry>

  makeKey: (sort: SortType, order: OrderType) => string
  getEntry: (sort: SortType, order: OrderType) => CacheEntry | undefined

  setEntry: (
    sort: SortType,
    order: OrderType,
    patch: Partial<Omit<CacheEntry, 'key' | 'sort' | 'order'>>
  ) => void

  setCounts: (
    sort: SortType,
    order: OrderType,
    countsById: Record<string, number>
  ) => void
  setLists: (sort: SortType, order: OrderType, lists: WrappedList[]) => void

  // NEW
  upsertList: (list: ListRow) => void
  renameList: (id: string, name: string) => void

  removeList: (listId: string) => void
  clear: () => void
}

const wrap = (data: any): WrappedList => ({ data })

export const useSubscriptionListsCacheStore = create<StoreState>()(
  persist(
    (set, get) => ({
      entries: {},

      makeKey: (sort, order) => `subscriptionLists:${sort}:${order}`,

      getEntry: (sort, order) => {
        const key = get().makeKey(sort, order)
        return get().entries[key]
      },

      setEntry: (sort, order, patch) => {
        const key = get().makeKey(sort, order)
        set(state => {
          const prev = state.entries[key]
          const next: CacheEntry = {
            key,
            sort,
            order,
            lists: patch.lists ?? prev?.lists ?? [],
            fetchedAt: patch.fetchedAt ?? prev?.fetchedAt,
            countsById: patch.countsById ?? prev?.countsById ?? {},
            updatedAt: patch.updatedAt ?? Date.now(),
          }
          return { entries: { ...state.entries, [key]: next } }
        })
      },

      setCounts: (sort, order, countsById) => {
        get().setEntry(sort, order, { countsById, updatedAt: Date.now() })
      },

      setLists: (sort, order, lists) => {
        get().setEntry(sort, order, { lists, updatedAt: Date.now(),
        fetchedAt: Date.now() })
      },

      //  Add list to every cached view (any sort/order the user might be on)
      upsertList: (list: ListRow) => {
        const safe: ListRow = {
          ...list,
          // counts not known immediately
          count: typeof list.count === 'number' ? list.count : 0,
          avatars: Array.isArray(list.avatars) ? list.avatars : [],
        }

        set(state => {
          const nextEntries: Record<string, CacheEntry> = { ...state.entries }

          for (const [key, entry] of Object.entries(nextEntries)) {
            const existingIdx = (entry.lists ?? []).findIndex(w => w?.data?.id === safe.id)

            let nextLists: WrappedList[]
            if (existingIdx >= 0) {
              nextLists = [...entry.lists]
              nextLists[existingIdx] = wrap({ ...(nextLists[existingIdx]?.data ?? {}), ...safe })
            } else {
              // insert at top; your finalLists pins "All" anyway
              nextLists = [wrap(safe), ...(entry.lists ?? [])]
            }

            const nextCounts = { ...(entry.countsById ?? {}) }
            if (nextCounts[safe.id] == null) nextCounts[safe.id] = safe.count ?? 0

            nextEntries[key] = {
              ...entry,
              lists: nextLists,
              countsById: nextCounts,
              updatedAt: Date.now(),
            }
          }

          return { entries: nextEntries }
        })
      },

      renameList: (id: string, name: string) => {
        set(state => {
          const nextEntries: Record<string, CacheEntry> = { ...state.entries }

          for (const [key, entry] of Object.entries(nextEntries)) {
            const nextLists = (entry.lists ?? []).map(w => {
              if (w?.data?.id === id) return wrap({ ...(w.data ?? {}), name })
              return w
            })

            nextEntries[key] = { ...entry, lists: nextLists, updatedAt: Date.now() }
          }

          return { entries: nextEntries }
        })
      },

      removeList: (listId: string) => {
        set(state => {
          const nextEntries: Record<string, CacheEntry> = {}

          for (const [key, entry] of Object.entries(state.entries)) {
            const nextLists = (entry.lists ?? []).filter(w => w?.data?.id !== listId)
            // eslint-disable-next-line @typescript-eslint/no-unused-vars
            const { [listId]: _removed, ...nextCounts } = entry.countsById ?? {}

            nextEntries[key] = {
              ...entry,
              lists: nextLists,
              countsById: nextCounts,
              updatedAt: Date.now(),
            }
          }

          return { entries: nextEntries }
        })
      },

      clear: () => set({ entries: {} }),
    }),
    {
      name: 'subscription-lists-cache-v1',
      version: 2,
      partialize: state => ({ entries: state.entries }),
    }
  )
)
